<template>
  <USelectMenu
    v-if="items.length"
    v-model="_discount"
    :items="items"
    label-key="name"
    :by="compareItems"
    :placeholder="resolvedPlaceholder"
    :filter-fields="['name']"
    class="min-w-52"
  >
    <template #default="{ modelValue: selected }">
      <div v-if="selected && !isNoneItem(selected)" class="flex items-center justify-between w-full h-full text-sm">
        <p class="text-sm">
          {{ isVoucherItem(selected) ? selected.name : selected.pass?.name }}
        </p>

        <o-chip size="xs" variant="success" flat class="mr-2">
          {{ isVoucherItem(selected) ? $t('general.promoCode') : $t('general.pass') }}
        </o-chip>
      </div>
      <span v-else class="truncate text-sm opacity-70">
        {{ resolvedPlaceholder }}
      </span>
    </template>

    <template #item="{ item }">
      <div v-if="isVoucherItem(item)" class="w-full flex items-center justify-between">
        <div>
          <p>
            {{ item.name }}
          </p>
          <p class="opacity-80">
            <template v-if="item.discountType === 0">
              {{ $currency.format(item.discountAmount) }}
            </template>

            <template v-if="item.discountType === 1">
              {{ item.discountAmount }}%
            </template>
          </p>
        </div>

        <o-chip size="xs" variant="success" flat>
          {{ $t('general.promoCode') }}
        </o-chip>
      </div>

      <div v-else-if="isCustomerPassItem(item)" class="w-full flex items-center justify-between">
        <div>
          <p>
            {{ item.pass.name }}
          </p>
          <p class="opacity-80">
            {{ item.infoLabel }}
          </p>
        </div>

        <o-chip size="xs" variant="success" flat>
          {{ $t('general.pass') }}
        </o-chip>
      </div>

      <div v-else class="w-full flex items-center justify-between">
        <div>
          <p>
            {{ item.name }}
          </p>
        </div>
      </div>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import { Voucher } from '@/_api/models/voucher'
import { CustomerPass } from '@/_api/models/customerPass'

const { $i18n } = useNuxtApp()

interface Props {
  voucher?: object | null
  customerPass?: object | null
  vouchers?: any[]
  customerPasses?: any[]
  service: Record<string, any>
  employee: Record<string, any>
  placeholder?: string
  autoSelect?: boolean
}

interface Emits {
  (e: 'update:voucher', value: any): void
  (e: 'update:customerPass', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  voucher: null,
  customerPass: null,
  vouchers: () => [],
  customerPasses: () => [],
  placeholder: undefined,
  autoSelect: true,
})

const emit = defineEmits<Emits>()

const resolvedPlaceholder = computed(() => {
  return props.placeholder ?? ($i18n.t('discount.select') as string)
})

const noneItem = computed(() => ({
  _sentinel: true,
  name: $i18n.t('general.none') as string,
}))

function isNoneItem(item: any): boolean {
  return item?._sentinel === true
}

function isVoucherItem(item: any): boolean {
  return item instanceof Voucher || item?.idVoucher != null
}

function isCustomerPassItem(item: any): boolean {
  return item instanceof CustomerPass || item?.idCustomerPass != null
}

function compareItems(a: any, b: any): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (a._sentinel && b._sentinel) return true
  if (isVoucherItem(a) && isVoucherItem(b)) return a.idVoucher === b.idVoucher
  if (isCustomerPassItem(a) && isCustomerPassItem(b)) return a.idCustomerPass === b.idCustomerPass
  return false
}

const _discount = computed({
  get () {
    const value = props.customerPass || props.voucher
    return value || noneItem.value
  },
  set (value) {
    if (isNoneItem(value)) {
      emit('update:voucher', null)
      emit('update:customerPass', null)
    } else if (isVoucherItem(value)) {
      emit('update:voucher', value)
      emit('update:customerPass', null)
    } else if (isCustomerPassItem(value)) {
      emit('update:voucher', null)
      emit('update:customerPass', value)
    } else {
      emit('update:voucher', null)
      emit('update:customerPass', null)
    }
  },
})

const items = computed(() => {
  const voucherList = props.vouchers || []
  const passList = props.customerPasses || []

  const opts: any[] = [noneItem.value, ...voucherList, ...passList]

  return opts
})

function applyAutoSelect() {
  if (!props.autoSelect) {
    return
  }

  if (items.value.length > 1) {
    _discount.value = items.value[1]
  }
}

watch(() => props.customerPasses, (newValue, oldValue) => {
  if (newValue?.length !== oldValue?.length) {
    applyAutoSelect()
  }
})

watch(() => props.vouchers, (newValue, oldValue) => {
  if (newValue?.length !== oldValue?.length) {
    applyAutoSelect()
  }
})

onMounted(() => {
  applyAutoSelect()
})
</script>
